import { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ACTION_TYPE,
  CommonStepOne,
  CommonStepThree,
  CommonStepTwo,
  DIGITAL_TWIN_ACTION,
  QUERYPARAMS,
} from "../common";
import { StationSyncStatus, ViewCards } from "../components";
import useStationStatus from "./useStationStatus";
import { useStepperContext } from "../StepperContext";
import { LoaderButton, CircularProgress } from "../../mui";
import useManualSwap from "../hooks/use-manual-swap";
import DoorPanel from "./DoorPanel";
import InBatterySelect from "../components/InBatterySelect";
import { useAuth } from "../../../contexts/AuthContext";
import {
  ActiveSwapsSidebar,
  LeavePageGuard,
  SwapAccessDialog,
} from "../../../components";
import useSwapSession from "../../../hooks/useSwapSession";
import AutoSubmitToast from "./AutoSubmitToast";
import BatteryNotDetectedDialog from "./BatteryNotDetectedDialog";
import useSwapAccess from "../../../hooks/useSwapAccess";
import { WarningAmberRounded } from "@mui/icons-material";

const STEP_PRIORITY = {
  DOOR_OPENED_IN: 1,
  DOOR_CLOSED_IN: 2,
  BATTERY_DETECTED_IN: 3,
  DOOR_OPENED_OUT: 4,
  DOOR_CLOSED_OUT: 5,
  SUBMITTING: 6,
};

const Section = ({ title, disabled = false, children }) => (
  <Stack
    gap={2}
    aria-disabled={disabled}
    sx={{
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? "none" : "auto",
      transition: "opacity 0.2s",
    }}
  >
    <Typography variant="h6">{title}</Typography>
    {children}
  </Stack>
);

export default function DigitalTwinSwap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, updateData, handleReset } = useStepperContext();
  const { isLoading, onSubmitManualSwap } = useManualSwap();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [autoSubmitStatus, setAutoSubmitStatus] = useState(null);
  const [swapInProgress, setSwapInProgress] = useState(false);
  const [outDoorOpened, setOutDoorOpened] = useState(false);
  const [autoSubmitDone, setAutoSubmitDone] = useState(false);
  const [accessOverridden, setAccessOverridden] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);

  const userEmail = user?.email || "Unknown User";

  const sessionCreatedRef = useRef(false);
  const currentStepRef = useRef(0);
  const doorCloseListenerRef = useRef(null);

  const reasonDone =
    data.swapReason === "Other" ? !!data.customReason : !!data.swapReason;
  const detailsDone = !!(
    data.partnerId &&
    data.driverId &&
    data.deviceId &&
    data.isDriverDetails
  );
  const swapStarted = !!data.batteryInsertionStartFromDigitalTwin;
  const batteryIdDone = !!data.insertedBatteryId;
  const inputsDone = reasonDone && detailsDone && batteryIdDone;
  const inDone = !!data.batteryInsertionCompleteFromDigitalTwin;

  const swapAccess = useSwapAccess({
    stationId: detailsDone ? data.deviceId : null,
    driverId: detailsDone ? data.driverId : null,
  });

  const accessBlocked = swapAccess.isDenied && !accessOverridden;

  const station = useStationStatus(detailsDone ? data.deviceId : null);

  const {
    allSessions,
    otherSessions,
    createSession,
    updateSession,
    removeSession,
  } = useSwapSession({
    deviceId: detailsDone ? data.deviceId : null,
    userEmail,
  });

  useEffect(() => {
    updateData("swapType", QUERYPARAMS.DIGITAL_TWIN);
  }, []);

  useEffect(() => {
    setAccessOverridden(false);
    setAccessDialogOpen(false);
  }, [data.deviceId, data.driverId]);

  useEffect(() => {
    if (swapAccess.isDenied && !accessOverridden) {
      setAccessDialogOpen(true);
    }
  }, [swapAccess.isDenied, accessOverridden]);
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    return () => {
      if (doorCloseListenerRef.current) {
        clearTimeout(doorCloseListenerRef.current);
        doorCloseListenerRef.current = null;
      }
    };
  }, []);

  const submitSwap = async (
    doorCloseConf = ACTION_TYPE.MANUAL,
    isAutoSubmit = false,
  ) => {
    const d = dataRef.current;

    await updateSession({
      step: "SUBMITTING",
      stepLabel: "Submitting swap",
      insertedBatteryId: d.insertedBatteryIdDT || d.insertedBatteryId || null,
      takenBatteryId: d.takenBatteryIdDT || null,
    });

    const payload = {
      deviceId: d.deviceId,
      partnerId: d.partnerId,
      driverId: d.driverId,
      manualSwapType: QUERYPARAMS.DIGITAL_TWIN,
      reasonForSwap: d.customReason || d.swapReason,
      insertedBatteryId: d.insertedBatteryIdDT || d.insertedBatteryId,
      insertedBatterySoc: d.insertedBatterySocDT || 0,
      insertedBatteryDoorCloseConf: d.insertedBatteryDoorCloseConf,
      batteryInsertedDetectionConf:
        d.batteryInsertedDetectionConf || ACTION_TYPE.MANUAL,
      batteryInsertedIntoDoor: d.batteryInsertedIntoDoor,
      takenBatteryId: d.takenBatteryIdDT,
      takenBatterySoc: d.takenBatterySocDT,
      batteryTakenFromDoor: d.batteryTakenFromDoor,
      takenBatteryDoorCloseConf: doorCloseConf,
      swapInitiatedBy: userEmail,
    };

    try {
      await onSubmitManualSwap(payload);
      await removeSession();
      if (!isAutoSubmit) {
        setSubmitted(true);
      }
      return true;
    } catch (error) {
      console.error("Swap submission failed:", error);
      await removeSession();
      return false;
    }
  };

  const handleSessionStep = async (stepData) => {
    const d = dataRef.current;
    const newPriority = STEP_PRIORITY[stepData.step] || 0;

    if (newPriority <= currentStepRef.current) {
      return;
    }

    currentStepRef.current = newPriority;

    if (stepData.isFirstDoor && !sessionCreatedRef.current) {
      sessionCreatedRef.current = true;
      setSwapInProgress(true);
      await createSession({
        driverId: d.driverId,
        partnerId: d.partnerId,
        driverName: d.driverName || d.driverId,
        doorNumber: stepData.doorNumber,
        step: stepData.step,
        stepLabel: stepData.stepLabel,
      });
      return;
    }

    const updatePayload = {
      step: stepData.step,
      stepLabel: stepData.stepLabel,
    };

    if (stepData.doorNumber !== undefined)
      updatePayload.doorNumber = stepData.doorNumber;
    if (stepData.batteryId !== undefined)
      updatePayload.batteryId = stepData.batteryId;

    if (stepData.step === "BATTERY_DETECTED_IN") {
      updatePayload.insertedBatteryId =
        stepData.batteryId ||
        d.insertedBatteryIdDT ||
        d.insertedBatteryId ||
        null;
    }

    if (
      stepData.step === "DOOR_OPENED_OUT" ||
      stepData.step === "DOOR_CLOSED_OUT"
    ) {
      updatePayload.takenBatteryId = d.takenBatteryIdDT || null;
      updatePayload.insertedBatteryId =
        d.insertedBatteryIdDT || d.insertedBatteryId || null;
    }

    await updateSession(updatePayload);
  };

  const handleOutDoorOpened = async () => {
    if (autoSubmitDone) return;

    setOutDoorOpened(true);

    const d = dataRef.current;

    const currentId = d.insertedBatteryIdDT || d.insertedBatteryId;
    const takenId = d.takenBatteryIdDT;
    const isSameBattery = !!takenId && takenId === currentId;
    const isDetectionConfirmed = !!d.batteryInsertedDetectionConf;

    if (!currentId || isSameBattery || !takenId || !isDetectionConfirmed) {
      setDialogOpen(true);
      return;
    }

    setAutoSubmitDone(true);
    setAutoSubmitStatus("submitting");

    const success = await submitSwap(ACTION_TYPE.AUTO, true);

    if (success) {
      setAutoSubmitStatus(null);
      setSubmitted(true);
    } else {
      setAutoSubmitStatus("failed");
      setAutoSubmitDone(false);
      setTimeout(() => setAutoSubmitStatus(null), 3000);
    }
  };

  const handleMainSubmit = async () => {
    const d = dataRef.current;
    const currentId = d.insertedBatteryIdDT || d.insertedBatteryId;
    const takenId = d.takenBatteryIdDT;
    const isSameBattery = !!takenId && takenId === currentId;
    const isDetectionConfirmed = !!d.batteryInsertedDetectionConf;

    if (!currentId || isSameBattery || !isDetectionConfirmed) {
      setDialogOpen(true);
      return;
    }

    await submitSwap(d.takenBatteryDoorCloseConf || ACTION_TYPE.MANUAL, false);
  };

  const handleDialogSubmit = async () => {
    setDialogOpen(false);
    await submitSwap(
      data.takenBatteryDoorCloseConf || ACTION_TYPE.MANUAL,
      false,
    );
  };

  const handleSwapAgain = () => {
    setSubmitted(false);
    setSwapInProgress(false);
    setOutDoorOpened(false);
    setAutoSubmitDone(false);
    setAutoSubmitStatus(null);
    setAccessOverridden(false);
    setAccessDialogOpen(false);
    sessionCreatedRef.current = false;
    currentStepRef.current = 0;
    handleReset();
  };

  if (submitted) {
    return (
      <Stack gap={3}>
        <ViewCards view="all" />
        <Box>
          <Button variant="contained" onClick={handleSwapAgain}>
            Swap Again
          </Button>
        </Box>
      </Stack>
    );
  }

  const insertedId = data.insertedBatteryIdDT || data.insertedBatteryId;
  const sameBattery =
    !!data.takenBatteryIdDT && data.takenBatteryIdDT === insertedId;

  return (
    <Stack gap={4}>
      <LeavePageGuard when={swapInProgress && !submitted} />

      <ActiveSwapsSidebar sessions={allSessions} />

      <AutoSubmitToast status={autoSubmitStatus} />

      <Section title="Swap reason" disabled={swapStarted}>
        <CommonStepOne />
      </Section>

      <Section title="Partner and driver" disabled={!reasonDone || swapStarted}>
        <CommonStepTwo index={1} />
      </Section>

      {swapAccess.isError && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "#FFFCF5",
            border: "1px solid #FEF0C7",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberRounded sx={{ fontSize: 18, color: "#DC6803" }} />
            <Typography sx={{ fontSize: 13, color: "#93370D" }}>
              Swap access check failed: {swapAccess.message}. Proceeding with
              swap.
            </Typography>
          </Box>
        </Box>
      )}

      <Section
        title="Battery given by driver"
        disabled={
          !reasonDone ||
          !detailsDone ||
          !!data.insertedBatteryIdDT ||
          accessBlocked ||
          swapAccess.isLoading
        }
      >
        <CommonStepThree index={2} />
        {data.insertedBatteryIdDT && (
          <Typography variant="body2" sx={{ color: "#079B27" }}>
            Auto detected in-battery: {data.insertedBatteryIdDT}
          </Typography>
        )}
      </Section>

      {(inputsDone || swapStarted) && (
        <Stack gap={4}>
          {!station.isReady ? (
            <CircularProgress />
          ) : (
            <>
              <StationSyncStatus
                syncTimestamp={station.syncTimestamp}
                isOnline={station.isOnline}
                stationIMEI={data.deviceId}
                partnerId={data.partnerId}
              />

              <DoorPanel
                action={DIGITAL_TWIN_ACTION.IN_BATTERY}
                station={station}
                onSessionStep={handleSessionStep}
                otherSessions={otherSessions}
              />
              {inDone && (
                <DoorPanel
                  action={DIGITAL_TWIN_ACTION.OUT_BATTERY}
                  station={station}
                  onSessionStep={handleSessionStep}
                  otherSessions={otherSessions}
                  onOutDoorOpened={handleOutDoorOpened}
                />
              )}
            </>
          )}

          {data.swapErrorRes && (
            <Typography variant="body2" color="error">
              {`${data.swapErrorRes.code}: ${data.swapErrorRes.message}`}
            </Typography>
          )}

          {!autoSubmitDone && (
            <Box>
              <LoaderButton
                variant="contained"
                onClick={handleMainSubmit}
                isLoading={isLoading}
                disabled={!data.takenBatteryIdDT || !outDoorOpened}
                sx={{ width: "auto", minWidth: "150px" }}
              >
                Submit
              </LoaderButton>
            </Box>
          )}
        </Stack>
      )}

      <BatteryNotDetectedDialog
        open={dialogOpen}
        doorNumber={Number(data.batteryInsertedIntoDoor) + 1}
        batteryId={data.insertedBatteryId}
        isDetected={!!data.insertedBatteryIdDT}
        isLoading={isLoading}
        batterySelect={<InBatterySelect />}
        onWait={() => setDialogOpen(false)}
        onSubmit={handleDialogSubmit}
      />

      <SwapAccessDialog
        open={accessDialogOpen}
        message={swapAccess.message}
        denialReason={swapAccess.denialReason}
        onGoBack={() => {
          handleSwapAgain();
        }}
        onProceed={() => {
          setAccessDialogOpen(false);
          setAccessOverridden(true);
        }}
      />
    </Stack>
  );
}
